<template>
  <span>
    <!-- Card header icon-button variant — matches v-btn icon flat pattern in BaseCard -->
    <v-tooltip v-if="variant === 'icon'" top>
      <template #activator="{ on }">
        <v-btn
          icon
          flat
          depressed
          fab
          :href="graphUrl"
          target="_blank"
          rel="noopener noreferrer"
          v-on="on"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle
              cx="9"
              cy="9"
              r="3"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <circle cx="3" cy="3" r="1.5" fill="currentColor" />
            <circle cx="15" cy="3" r="1.5" fill="currentColor" />
            <circle cx="3" cy="15" r="1.5" fill="currentColor" />
            <circle cx="15" cy="15" r="1.5" fill="currentColor" />
            <line
              x1="4.06"
              y1="4.06"
              x2="7.17"
              y2="7.17"
              stroke="currentColor"
              stroke-width="1.3"
            />
            <line
              x1="13.94"
              y1="4.06"
              x2="10.83"
              y2="7.17"
              stroke="currentColor"
              stroke-width="1.3"
            />
            <line
              x1="4.06"
              y1="13.94"
              x2="7.17"
              y2="10.83"
              stroke="currentColor"
              stroke-width="1.3"
            />
            <line
              x1="13.94"
              y1="13.94"
              x2="10.83"
              y2="10.83"
              stroke="currentColor"
              stroke-width="1.3"
            />
          </svg>
        </v-btn>
      </template>
      <span>{{ tooltipText }}</span>
    </v-tooltip>

    <!-- Default inline link variant with tooltip -->
    <v-tooltip
      v-else-if="tooltip"
      :right="tooltipRight"
      :bottom="!tooltipRight"
      open-delay="300"
    >
      <template #activator="{ on, attrs }">
        <a
          class="im-link-btn"
          :href="graphUrl"
          :target="target"
          rel="noopener noreferrer"
          v-bind="attrs"
          v-on="on"
          @click.stop
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle
              cx="9"
              cy="9"
              r="3"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <circle cx="3" cy="3" r="1.5" fill="currentColor" />
            <circle cx="15" cy="3" r="1.5" fill="currentColor" />
            <circle cx="3" cy="15" r="1.5" fill="currentColor" />
            <circle cx="15" cy="15" r="1.5" fill="currentColor" />
            <line
              x1="4.06"
              y1="4.06"
              x2="7.17"
              y2="7.17"
              stroke="currentColor"
              stroke-width="1.3"
            />
            <line
              x1="13.94"
              y1="4.06"
              x2="10.83"
              y2="7.17"
              stroke="currentColor"
              stroke-width="1.3"
            />
            <line
              x1="4.06"
              y1="13.94"
              x2="7.17"
              y2="10.83"
              stroke="currentColor"
              stroke-width="1.3"
            />
            <line
              x1="13.94"
              y1="13.94"
              x2="10.83"
              y2="10.83"
              stroke="currentColor"
              stroke-width="1.3"
            />
          </svg>
          <span v-if="showLabel && name" class="im-link-label">{{ name }}</span>
        </a>
      </template>
      <span>{{ tooltipText }}</span>
    </v-tooltip>

    <!-- Default inline link variant without tooltip -->
    <a
      v-else
      class="im-link-btn"
      :href="graphUrl"
      :target="target"
      rel="noopener noreferrer"
      @click.stop
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="4"
          r="2.5"
          stroke="currentColor"
          stroke-width="1.5"
        />
        <circle
          cx="4"
          cy="20"
          r="2.5"
          stroke="currentColor"
          stroke-width="1.5"
        />
        <circle
          cx="20"
          cy="20"
          r="2.5"
          stroke="currentColor"
          stroke-width="1.5"
        />
        <path
          d="M12 6.5v5.5M12 12l-6.5 6M12 12l6.5 6"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
        />
      </svg>
      <span v-if="showLabel && name" class="im-link-label">{{ name }}</span>
    </a>
  </span>
</template>

<script>
import SbBaseUrl from '../../plugins/SbBaseUrl.js';

export default {
  name: 'IdentityMapLink',

  props: {
    id: { type: [String, Number], required: true },
    mode: {
      type: String,
      default: 'node',
      validator: (v) => ['node', 'identity'].includes(v),
    },
    name: { type: String, default: '' },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'icon'].includes(v),
    },
    focalName: { type: String, default: '' },
    focalType: { type: String, default: '' },
    showLabel: { type: Boolean, default: false },
    target: { type: String, default: '_blank' },
    tooltip: { type: Boolean, default: true },
    tooltipRight: { type: Boolean, default: false },
  },

  computed: {
    graphUrl() {
      const params = new URLSearchParams({
        focal: this.id,
        mode: this.mode,
      });
      if (this.focalName) params.set('focalName', this.focalName);
      if (this.focalType) params.set('focalType', this.focalType);
      return `${SbBaseUrl.baseUrl}/IdentityMap?${params.toString()}`;
    },

    tooltipText() {
      return 'View in Identity Map';
    },
  },
};
</script>

<style scoped>
.im-link-btn {
  display: inline-flex;
  align-items: center;
  color: var(--v-primary-base, #1976d2);
  cursor: pointer;
  text-decoration: none;
  vertical-align: middle;
  transition: color 0.12s;
}

.im-link-btn:hover {
  color: var(--v-primary-darken1, #1565c0);
  text-decoration: none;
}

.im-link-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.im-link-label {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

html.dark .im-link-btn {
  color: var(--v-primary-lighten1, #42a5f5);
}

html.dark .im-link-btn:hover {
  color: var(--v-primary-lighten2, #64b5f6);
}
</style>
